<script lang="ts">
  import {
    computed,
    defineComponent,
    inject,
    getCurrentInstance,
    ref,
    reactive,
    onBeforeMount
  } from 'vue'
  import { getComponentNamespace, getNamespace } from '../../../utils/global-config'
  import { useIndex } from '../../../hooks/use-index'
  import { BnIconCheck } from '../../icon'
  import { stepProps } from './props'
  import type { StepsContext } from './context'
  import { stepsInjectionKey } from './context'

  export default defineComponent({
    name: getComponentNamespace('Step'),
    components: { BnIconCheck },
    props: stepProps,
    emits: ['close'],
    setup(props, { slots }) {
      // 定义组件命名
      const ns = getNamespace('steps-item')
      // 获取父级组件provide数据
      const context = inject(stepsInjectionKey, {} as StepsContext)
      const { stepsType, parentCls, addItem, removeItem, onClick } = context
      // 获取组件内部实例
      const instance = getCurrentInstance()
      // Ref
      const itemRef = ref<HTMLElement>()
      // 点颜色
      const innerDotColor = ref(props.dotColor)
      // 内部Index
      const { innerIndex } = useIndex({
        itemRef,
        selector: `.${ns}`,
        parentClassName: parentCls
      })
      // 当前step步数
      const stepNumber = computed(() => innerIndex.value + 1)
      // 是否为最后一个
      const isLast = computed((): boolean => {
        const { count } = context as StepsContext
        return innerIndex.value + 1 === count
      })
      // 组件class
      const cls = computed(() => [
        ns
        // stepsType && `is-${stepsType}`,
        // context.innerCurrent === stepNumber.value ? 'is-active' : ''
      ])
      const stepStatusCls = computed(() => [`is-${stepStatus.value.toLocaleLowerCase()}`])
      // PREV NEXT
      const stepStatus = computed(() => {
        if (context.innerCurrent > stepNumber.value) {
          return 'COMPLETE'
        } else if (context.innerCurrent === stepNumber.value) {
          return 'ACTIVE'
        } else {
          return 'WAITING'
        }
      })
      if (instance) {
        addItem(
          instance.uid,
          reactive({
            step: stepNumber
          })
        )
      }
      /**
       * @description 注册一个钩子，在组件被挂载之前被调用。
       */
      onBeforeMount(() => {
        removeItem(instance?.uid as number)
      })
      /**
       * @description step点击切换
       * @param e
       */
      const handleClick = (e: Event) => {
        if (!props.disabled) {
          onClick(stepNumber.value, e)
        }
      }
      // lineType
      const lineType = computed(() => [
        {
          borderBottomStyle: context?.lineType
        }
      ])
      // slotsIcon
      const iconCls = computed(() =>
        slots?.icon?.() ? [`${ns}__icon`, ...stepStatusCls.value] : [`${ns}__icon`]
      )
      return {
        ns,
        cls,
        stepsType,
        itemRef,
        stepNumber,
        handleClick,
        isLast,
        lineType,
        iconCls,
        innerDotColor,
        stepStatus,
        stepStatusCls
      }
    }
  })
</script>

<template>
  <div ref="itemRef" :class="cls" @click="handleClick">
    <div v-if="stepsType === 'icon' && !icon" :class="`${ns}__node`">
      <div :class="iconCls">
        <slot name="icon">
          <i class="icon" :style="[{ backgroundColor: innerDotColor }]"></i>
        </slot>
      </div>
    </div>
    <div v-if="stepsType === 'icon'" :class="`${ns}__dot`">
      <div class="dot" :class="stepStatusCls"></div>
      <div v-if="!isLast" :class="`${ns}__dot__line`" :style="lineType"></div>
    </div>
    <div v-else :class="`${ns}__serial-number`">
      <div class="step-mark" :class="stepStatusCls">
        <div v-if="stepStatus === 'COMPLETE'" class="diy-icon">
          <slot name="complete-icon">
            <BnIconCheck></BnIconCheck>
          </slot>
        </div>
        <div v-else class="number">{{ stepNumber }}</div>
      </div>
      <div v-if="!isLast" :class="`${ns}__serial-number__line`" :style="lineType"></div>
    </div>
    <div :class="[`${ns}__content`, ...stepStatusCls]">
      <div :class="`${ns}__title`">
        <slot>{{ title }}</slot>
      </div>
      <div v-if="description" :class="[`${ns}__description`]">
        <slot name="description">{{ description }}</slot>
      </div>
    </div>
  </div>
</template>
./context
